import type { ReactNode } from 'react'
import { TableCell } from '@/components/Table'
import cx from 'classnames'
import type { TransactionSourceType } from '@/features/payouts/transaction-source-type'
import { match } from '@/utils/pattern-match-util'
import { formatTransactionReason } from '@/features/payouts/transaction-formatters'
import { Amount } from '@/features/payouts/components/Amount'
import type { UserTransactionReason } from '@/features/payouts/user-transaction-reason'
import styles from './PayoutTransactionsTableCells.module.css'
import type { TransactionFlag } from '@/features/payouts/transaction-flag'
import { addSeparators } from '@/utils/array-util'
import { Icon } from '@/components/Icon'
import { Tooltip } from '@/components/Tooltip'

interface ContextCellProps {
  source: TransactionSourceType
  userTransactionReason?: UserTransactionReason | null
  userTransactionMemo?: string | null
  jobTransactionJobId?: string | null
  lastFailureDescription?: string | null
}

/**
 * Context cell.
 */
export function ContextCell({
  source,
  userTransactionReason: reason,
  userTransactionMemo: memo,
  jobTransactionJobId: jobId,
  lastFailureDescription,
}: ContextCellProps) {
  const warning = lastFailureDescription && (
    <>
      Previous payout failed
      <br />
      {lastFailureDescription}
    </>
  )
  return (
    <TableCell>
      <div className={styles.contextCell}>
        {match(source, {
          Unknown: () => (
            <>
              <ContextCellTitle warning={warning}>
                Unknown source
              </ContextCellTitle>
            </>
          ),
          User: () => (
            <>
              <ContextCellTitle warning={warning}>
                {formatTransactionReason(reason)}
              </ContextCellTitle>
              {memo && <ContextCellSubtitle>{memo}</ContextCellSubtitle>}
            </>
          ),
          Job: () => (
            <>
              <ContextCellTitle warning={warning}>Job payout</ContextCellTitle>
              <ContextCellSubtitle identifier>{jobId}</ContextCellSubtitle>
            </>
          ),
        })}
      </div>
    </TableCell>
  )
}

interface ContextCellTitleProps {
  children: ReactNode
  warning?: ReactNode
}

function ContextCellTitle({ children, warning }: ContextCellTitleProps) {
  return (
    <div className={styles.contextCellTitle}>
      {children}
      {warning && (
        <div className={styles.contextCellTitleWarning}>
          <Tooltip placement="top" content={warning}>
            <Icon size={14} name="info-blue" />
          </Tooltip>
        </div>
      )}
    </div>
  )
}

interface ContextCellSubtitleProps {
  children: ReactNode
  identifier?: boolean
}

function ContextCellSubtitle({
  children,
  identifier,
}: ContextCellSubtitleProps) {
  return (
    <div
      className={cx(
        styles.contextCellSubtitle,
        identifier && styles.identifier,
      )}
    >
      {children}
    </div>
  )
}

export interface AmountCellProps {
  amount: number
  neutral?: boolean
}

/**
 * Amount Cell
 */
export function AmountCell({ amount, neutral }: AmountCellProps) {
  return (
    <TableCell align="right">
      <div className={styles.amountCell}>
        <Amount neutral={neutral} value={amount} />
      </div>
    </TableCell>
  )
}

export interface FlagsCellProps {
  flags: TransactionFlag[]
}

/**
 * Flags Cell
 */
export function FlagsCell({ flags }: FlagsCellProps) {
  const flagElements = flags.map((flag) => {
    const [flagClassName, flagText] = match(flag, {
      Unknown: () => [styles.flag, '(Unknown flag)'],
      JobCanceled: () => [styles.flag, 'Job canceled'],
      JobPayoutAutoAdjusted: () => [styles.flagInfo, 'Split auto-adjusted'],
    })
    return (
      <span key={flag} className={flagClassName}>
        {flagText}
      </span>
    )
  })

  return (
    <TableCell>
      <div className={styles.flagsCell}>
        {addSeparators(flagElements, () => (
          <>, </>
        ))}
      </div>
    </TableCell>
  )
}

export interface IdentifierCellProps {
  value: string | null | undefined
}

/**
 * Identifier Cell
 */
export function IdentifierCell({ value }: IdentifierCellProps) {
  return (
    <TableCell>
      <div className={styles.identifierCell}>{value}</div>
    </TableCell>
  )
}
